import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";

import useDropOffPlanner from "../../data/useDropOffPlanner";
import useLocationGroups from "../../data/useLocationGroups";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { FinancialDecision } from "../../models/Vehicle";
import {
  DropOffDayRange,
  DropOffSlot,
  DropOffSort,
  IDropOffDragItem,
  IDropOffLeadRow,
} from "../../models/DropOffPlanner";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import LoadingWheel from "../shared/LoadingWheel";
import DropOffDayColumn from "./DropOffDayColumn";
import DropOffPlannerToolbar from "./DropOffPlannerToolbar";
import DropOffStatsRow from "./DropOffStatsRow";
import printDropOffSchedule from "./printDropOffSchedule";
import UnscheduledDropOffsPanel from "./UnscheduledDropOffsPanel";

interface DropOffPlannerViewProps {
  currentLeadId?: string;
  containerClassName?: string;
}

const allocationDateKey = (iso: string | null): string | null => {
  if (!iso) return null;
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
};

const STORAGE_PREFIX = "dropOffPlanner";
const STORAGE_KEY = {
  sort: `${STORAGE_PREFIX}.sort`,
  dayRange: `${STORAGE_PREFIX}.dayRange`,
};

const readStored = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};

const readStoredDayRange = (fallback: DropOffDayRange): DropOffDayRange => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(STORAGE_KEY.dayRange);
  const allowed: DropOffDayRange[] = [1, 3, 5, 7];
  const parsed = value !== null ? Number(value) : NaN;
  return allowed.includes(parsed as DropOffDayRange) ? (parsed as DropOffDayRange) : fallback;
};

const writeStored = (key: string, value: string | number) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
};

export default function DropOffPlannerView({
  currentLeadId,
  containerClassName,
}: DropOffPlannerViewProps) {
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Drop Off");
  const isFocusedOnLead = !!currentLeadId;
  const isTme = CheckFeatureFlag("TME");

  const [sort, setSort] = useState<DropOffSort>(() =>
    readStored<DropOffSort>(STORAGE_KEY.sort, ["Oldest", "Newest"] as const, "Oldest")
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dayRange, setDayRange] = useState<DropOffDayRange>(() => readStoredDayRange(7));
  const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.local().startOf("day"));
  const [clearOpen, setClearOpen] = useState<boolean>(false);
  const [isClearingAll, setIsClearingAll] = useState<boolean>(false);

  const fromDate = selectedDate;
  const toDate = selectedDate.plus({ days: dayRange - 1 });
  const fromIso = fromDate.toISODate() ?? "";
  const toIso = toDate.toISODate() ?? "";

  const { unscheduled, scheduled, allocateLead, clearAllocation, clearDay } = useDropOffPlanner({
    from: fromIso,
    to: toIso,
  });
  const { availabilities: locationGroupAvailabilities } = useLocationGroups();

  useEffect(() => writeStored(STORAGE_KEY.sort, sort), [sort]);
  useEffect(() => writeStored(STORAGE_KEY.dayRange, dayRange), [dayRange]);

  // With TME enabled, only leads whose vehicle is marked Purchased are shown as unscheduled.
  const allUnscheduledLeads: IDropOffLeadRow[] = useMemo(() => {
    const rows = unscheduled.data?.data ?? [];
    return isTme ? rows.filter((l) => l.financialDecision === FinancialDecision.Purchased) : rows;
  }, [unscheduled.data, isTme]);
  const scheduledLeads: IDropOffLeadRow[] = scheduled.data?.data ?? [];

  const unscheduledLeads = useMemo(
    () =>
      isFocusedOnLead
        ? allUnscheduledLeads.filter((l) => l.id === currentLeadId)
        : allUnscheduledLeads,
    [allUnscheduledLeads, isFocusedOnLead, currentLeadId]
  );

  const dayColumns: string[] = useMemo(
    () =>
      Array.from(
        { length: dayRange },
        (_, i) => selectedDate.plus({ days: i }).toISODate() ?? ""
      ),
    [dayRange, selectedDate]
  );

  const leadsByDate = useMemo(() => {
    const map = new Map<string, IDropOffLeadRow[]>();
    scheduledLeads.forEach((l) => {
      const key = allocationDateKey(l.scheduledCollectionOn);
      if (!key) return;
      const list = map.get(key) ?? [];
      list.push(l);
      map.set(key, list);
    });
    return map;
  }, [scheduledLeads]);

  const leadsFor = (dateIso: string): IDropOffLeadRow[] => leadsByDate.get(dateIso) ?? [];

  const handleDrop = (item: IDropOffDragItem, dateIso: string, slot: DropOffSlot) => {
    if (
      allocationDateKey(item.currentScheduledOn) === dateIso &&
      item.currentSlot === slot
    ) {
      return;
    }

    allocateLead.mutate(
      {
        leadId: item.leadId,
        body: { scheduledOn: dateIso, slot },
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
      }
    );
  };

  const handleDropToUnscheduled = (item: IDropOffDragItem) => {
    if (!item.currentScheduledOn) return;

    clearAllocation.mutate(item.leadId, {
      onSuccess: deleteHandlers.onSuccess,
      onError: deleteHandlers.onError,
    });
  };

  const handleClearConfirm = async () => {
    setIsClearingAll(true);
    try {
      await Promise.all(dayColumns.map((date) => clearDay.mutateAsync({ date })));
      deleteHandlers.onSuccess();
      setClearOpen(false);
    } catch {
      deleteHandlers.onError();
    } finally {
      setIsClearingAll(false);
    }
  };

  const handlePrintDay = (dateIso: string) => {
    printDropOffSchedule([{ date: dateIso, leads: leadsFor(dateIso) }]);
  };

  const handlePrintRange = () => {
    printDropOffSchedule(
      dayColumns
        .map((date) => ({ date, leads: leadsFor(date) }))
        .filter((day) => day.leads.length > 0)
    );
  };

  const isLoading = unscheduled.isLoading || scheduled.isLoading;

  return (
    <>
      <ConfirmModal
        open={clearOpen}
        setOpen={setClearOpen}
        title="Clear drop off bookings"
        message={`Remove every drop off booking for ${
          dayColumns.length > 1
            ? `${fromDate.toFormat("ccc d LLL")} - ${toDate.toFormat("ccc d LLL")}`
            : fromDate.toFormat("ccc d LLL")
        }? This cannot be undone.`}
        confirmButtonText="Clear"
        onConfirm={handleClearConfirm}
        isLoading={isClearingAll}
      />

      <div className={containerClassName ?? "mt-2 flex flex-col gap-2 h-[calc(100vh-9rem)]"}>
        <DropOffPlannerToolbar
          sort={sort}
          setSort={setSort}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dayRange={dayRange}
          setDayRange={setDayRange}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClearAll={() => setClearOpen(true)}
          onPrintRange={handlePrintRange}
          isClearing={isClearingAll}
          hideClear={isFocusedOnLead}
        />

        {!isFocusedOnLead && (
          <DropOffStatsRow
            unscheduledLeads={allUnscheduledLeads}
            scheduledLeads={scheduledLeads}
            locationGroups={locationGroupAvailabilities.data?.data ?? []}
          />
        )}

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md">
            <LoadingWheel />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-12 gap-2 min-h-0">
            <div className="col-span-12 lg:col-span-3 xl:col-span-2 min-h-0">
              <UnscheduledDropOffsPanel
                leads={unscheduledLeads}
                isLoading={unscheduled.isLoading}
                searchTerm={searchTerm}
                sort={sort}
                onDropFromCalendar={handleDropToUnscheduled}
              />
            </div>

            <div className="col-span-12 lg:col-span-9 xl:col-span-10 min-h-0">
              <div className="h-full overflow-x-auto overflow-y-hidden">
                <div className="flex gap-2 h-full pr-2">
                  {dayColumns.map((dateIso) => (
                    <DropOffDayColumn
                      key={dateIso}
                      date={dateIso}
                      leads={leadsFor(dateIso)}
                      onDrop={handleDrop}
                      onPrint={handlePrintDay}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
